/**
 * Client-side preview of FormSpec's BOM line assignment, used to show
 * stand-in rows for empty type-level BOM line slots.
 *
 * `assignBomLines` mirrors FormSpec's `plan_item_bom_lines`
 * (form_spec/services/items.py), which runs `assign_lines`
 * (form_spec/services/bom_order.py) for an item BOM:
 *
 * 0. Process lines: processes with an instance line entry, and processes
 *    filling the item type's process slots (`child_type: "process"` in
 *    `item.item_type.bom_order_config`), reserve their lines first.
 *    Unfilled process slots never hold their line.
 * 1. Entries: a child with an instance line entry in the parent config
 *    (`item.bom_order_config`) or added through a slot stand-in
 *    (`slot_entry`) keeps its line.
 * 2. Slots: remaining children of a slotted item type fill that type's free
 *    slots in ascending line order, taking children in name order.
 * 3. Holds: item slots that are still free stay reserved (these become
 *    stand-ins).
 * 4. Auto: everything else gets contiguous numbers from 1, sorted by type
 *    name then name, skipping every line already taken.
 *
 * Unlike FormSpec, conflicting configuration never throws here; a child whose
 * line entry collides with a taken line is treated as having no entry.
 *
 * Stand-ins are computed at render time only. They are never inserted into
 * `bom[]`, because BOM paths and `bom_changes` are index based.
 */

export interface PreviewLine {
  lineNumber: number;
  hint: string | null;
  // The line came from a line entry or a slot (not an auto-assigned number).
  isConfigured: boolean;
}

export interface BomLinePreview {
  // Keyed by index into `parent.bom`; deleted children have no entry.
  lines: Record<number, PreviewLine>;
  unfilledSlots: BomSlot[];
}

export type ChildEntry = { child: BOMItem; originalIndex: number };

export type ChildrenTableRow =
  | {
      kind: "child";
      key: string;
      entry: ChildEntry;
      lineNumber: number | null;
      hint: string | null;
      // The line shown differs from the line currently stored in Arena.
      isTentative: boolean;
    }
  | { kind: "slot"; key: string; slot: BomSlot; lineNumber: number };

const isFormSpecItem = (item: ArenaOnlyItem | Item): item is Item =>
  "uuid" in item && "item_type" in item && !!item.item_type;

const childTypeUuid = (child: BOMItem): string | null =>
  isFormSpecItem(child.item) ? child.item.item_type.uuid : null;

const childTypeName = (child: BOMItem): string =>
  isFormSpecItem(child.item)
    ? child.item.item_type.name
    : child.item.item_type_name || "";

const isPositiveInt = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

// Python's str ordering (code points), not locale ordering, so the preview
// sorts exactly like FormSpec.
const compareStrings = (a: string, b: string): number =>
  a < b ? -1 : a > b ? 1 : 0;

const instanceEntries = (parent: ItemWithBOM): Record<string, number> =>
  (isFormSpecItem(parent.item) ? parent.item.bom_order_config : null) || {};

/**
 * Instance line entry for a child, if any: an explicit slot fill from the
 * editor, or the parent instance config entry for this child instance.
 */
export const getLineEntry = (
  parent: ItemWithBOM,
  child: BOMItem
): number | null => {
  if (!isFormSpecItem(child.item)) {
    return null;
  }
  if (child.slot_entry) {
    return isPositiveInt(child.line_number) ? child.line_number : null;
  }
  const entry = instanceEntries(parent)[child.item.uuid];
  return isPositiveInt(entry) ? entry : null;
};

type LineSlot = { child_type_uuid: string; line_number: number };

const slotsByType = <S extends LineSlot>(slots: S[]): Record<string, S[]> => {
  const byType: Record<string, S[]> = {};
  slots.forEach((slot) => {
    (byType[slot.child_type_uuid] = byType[slot.child_type_uuid] || []).push(
      slot
    );
  });
  Object.keys(byType).forEach((typeUuid) =>
    byType[typeUuid].sort((a, b) => a.line_number - b.line_number)
  );
  return byType;
};

/** The parent item type's child *process* slots (FormSpec `slots_for`). */
const processSlots = (parent: ItemWithBOM): TypeBomLineSlot[] => {
  const config = isFormSpecItem(parent.item)
    ? parent.item.item_type.bom_order_config
    : undefined;
  // Older FormSpec responses used a dict keyed by type uuid; ignore those.
  if (!Array.isArray(config)) {
    return [];
  }
  return config.filter(
    (slot) =>
      slot?.child_type === "process" &&
      !!slot.child_type_uuid &&
      isPositiveInt(slot.line_number)
  );
};

const processName = (proc: ProcessWithSteps): string =>
  proc.process?.name || proc.process?.process_type?.name || "";

/**
 * Lines taken by configured processes: instance line entries, then process
 * slots filled in process name order (FormSpec `assign_process_lines` lines
 * with `is_configured`). Deleted processes are skipped.
 */
export const configuredProcessLines = (parent: ItemWithBOM): number[] => {
  const entries = instanceEntries(parent);
  const deleted = new Set(
    (parent.process_changes || [])
      .filter((change) => change.is_deleted)
      .map((change) => change.index)
  );
  const processes = (parent.processes || [])
    .map((proc, index) => ({ proc, index }))
    .filter(({ index }) => !deleted.has(index));
  const used = new Set<number>();
  const configured = new Set<number>();

  processes.forEach(({ proc, index }) => {
    const uuid = proc.process?.uuid;
    const entry = uuid ? entries[uuid] : undefined;
    if (isPositiveInt(entry) && !used.has(entry)) {
      used.add(entry);
      configured.add(index);
    }
  });

  const byType = slotsByType(processSlots(parent));
  Object.keys(byType).forEach((typeUuid) => {
    const candidates = processes
      .filter(
        ({ proc, index }) =>
          !configured.has(index) &&
          proc.process?.process_type?.uuid === typeUuid
      )
      .sort(
        (a, b) =>
          compareStrings(
            processName(a.proc).toLowerCase(),
            processName(b.proc).toLowerCase()
          ) || a.index - b.index
      );
    const freeSlots = byType[typeUuid].filter(
      (slot) => !used.has(slot.line_number)
    );
    candidates.forEach(({ index }, position) => {
      const slot = freeSlots[position];
      if (!slot) {
        return;
      }
      used.add(slot.line_number);
      configured.add(index);
    });
  });

  return Array.from(used).sort((a, b) => a - b);
};

export const assignBomLines = (
  parent: ItemWithBOM,
  isDeleted: (bomIndex: number) => boolean = () => false
): BomLinePreview => {
  const children = parent.bom || [];
  const slots = (parent.bom_slots || []).filter((slot) =>
    isPositiveInt(slot.line_number)
  );
  const byType = slotsByType(slots);
  const used = new Set<number>(configuredProcessLines(parent));
  const lines: Record<number, PreviewLine> = {};
  const active = children
    .map((_, index) => index)
    .filter((index) => !isDeleted(index));

  // 1. Entries
  active.forEach((index) => {
    const child = children[index];
    const entry = getLineEntry(parent, child);
    if (entry === null || used.has(entry)) {
      return;
    }
    used.add(entry);
    const typeUuid = childTypeUuid(child);
    const slot = (typeUuid ? byType[typeUuid] || [] : []).find(
      (s) => s.line_number === entry
    );
    lines[index] = {
      lineNumber: entry,
      hint: slot?.hint ?? null,
      isConfigured: true,
    };
  });

  // 2. Slots
  Object.keys(byType).forEach((typeUuid) => {
    const candidates = active
      .filter(
        (index) =>
          !(index in lines) && childTypeUuid(children[index]) === typeUuid
      )
      .sort(
        (a, b) =>
          compareStrings(
            children[a].item.name.toLowerCase(),
            children[b].item.name.toLowerCase()
          ) || a - b
      );
    const freeSlots = byType[typeUuid].filter(
      (slot) => !used.has(slot.line_number)
    );
    candidates.forEach((index, position) => {
      const slot = freeSlots[position];
      if (!slot) {
        return;
      }
      used.add(slot.line_number);
      lines[index] = {
        lineNumber: slot.line_number,
        hint: slot.hint,
        isConfigured: true,
      };
    });
  });

  // 3. Holds
  const unfilledSlots = slots
    .filter((slot) => !used.has(slot.line_number))
    .sort((a, b) => a.line_number - b.line_number);
  unfilledSlots.forEach((slot) => used.add(slot.line_number));

  // 4. Auto
  const autoIndices = active
    .filter((index) => !(index in lines))
    .sort(
      (a, b) =>
        compareStrings(
          childTypeName(children[a]).toLowerCase(),
          childTypeName(children[b]).toLowerCase()
        ) ||
        compareStrings(
          children[a].item.name.toLowerCase(),
          children[b].item.name.toLowerCase()
        ) ||
        a - b
    );
  let counter = 1;
  autoIndices.forEach((index) => {
    while (used.has(counter)) {
      counter += 1;
    }
    lines[index] = { lineNumber: counter, hint: null, isConfigured: false };
    counter += 1;
  });

  return { lines, unfilledSlots };
};

/**
 * The saved layout: each child's stored Arena line/hint, plus the slots whose
 * line no child currently occupies.
 */
export const storedBomLines = (
  parent: ItemWithBOM,
  isDeleted: (bomIndex: number) => boolean = () => false
): BomLinePreview => {
  const children = parent.bom || [];
  const lines: Record<number, PreviewLine> = {};
  const occupied = new Set<number>();
  children.forEach((child, index) => {
    if (isDeleted(index) || !isPositiveInt(child.line_number)) {
      return;
    }
    occupied.add(child.line_number);
    lines[index] = {
      lineNumber: child.line_number,
      hint: child.hint ?? null,
      isConfigured: !!child.hint,
    };
  });
  const unfilledSlots = (parent.bom_slots || [])
    .filter(
      (slot) =>
        isPositiveInt(slot.line_number) && !occupied.has(slot.line_number)
    )
    .sort((a, b) => a.line_number - b.line_number);
  return { lines, unfilledSlots };
};

/**
 * Rows for the Children table: real children and slot stand-ins, ordered by
 * line number. Children without a line keep their relative order at the end.
 *
 * With `preview` the lines are what FormSpec will assign on save (used while
 * the parent's BOM has unsaved changes); otherwise the stored lines are used.
 */
export const buildChildrenTableRows = (
  parent: ItemWithBOM,
  entries: ChildEntry[],
  {
    isDeleted = () => false,
    preview = false,
    includeSlot = () => true,
  }: {
    isDeleted?: (bomIndex: number) => boolean;
    preview?: boolean;
    includeSlot?: (slot: BomSlot) => boolean;
  } = {}
): ChildrenTableRow[] => {
  const layout = preview
    ? assignBomLines(parent, isDeleted)
    : storedBomLines(parent, isDeleted);

  const rows: ChildrenTableRow[] = entries.map((entry) => {
    const line = layout.lines[entry.originalIndex];
    const stored = isPositiveInt(entry.child.line_number)
      ? entry.child.line_number
      : null;
    if (!line) {
      // Deleted (or unnumbered when not previewing): show what is stored.
      return {
        kind: "child",
        key: `child-${entry.originalIndex}`,
        entry,
        lineNumber: stored,
        hint: entry.child.hint ?? null,
        isTentative: false,
      };
    }
    return {
      kind: "child",
      key: `child-${entry.originalIndex}`,
      entry,
      lineNumber: line.lineNumber,
      hint: line.hint,
      isTentative: preview && line.lineNumber !== stored,
    };
  });

  layout.unfilledSlots.filter(includeSlot).forEach((slot) => {
    rows.push({
      kind: "slot",
      key: `slot-${slot.line_number}`,
      slot,
      lineNumber: slot.line_number,
    });
  });

  // Array.prototype.sort is stable, so unnumbered rows keep their order.
  return rows
    .map((row, position) => ({ row, position }))
    .sort((a, b) => {
      const lineA = a.row.lineNumber;
      const lineB = b.row.lineNumber;
      if (lineA === null && lineB === null) return a.position - b.position;
      if (lineA === null) return 1;
      if (lineB === null) return -1;
      return lineA - lineB || a.position - b.position;
    })
    .map(({ row }) => row);
};

const isBomIndexDeleted = (parent: ItemWithBOM, bomIndex: number): boolean =>
  (parent.bom_changes || []).some(
    (change) => change.is_deleted && change.index === bomIndex
  );

/**
 * UUIDs of the FormSpec items currently in `parent`'s BOM (deleted children
 * excluded). FormSpec rejects a BOM that lists the same part more than once,
 * so these cannot be added again.
 */
export const bomChildItemUuids = (
  parent: ItemWithBOM | null | undefined,
  { excludeIndex }: { excludeIndex?: number } = {}
): Set<string> => {
  const uuids = new Set<string>();
  (parent?.bom || []).forEach((child, index) => {
    if (index === excludeIndex || isBomIndexDeleted(parent!, index)) {
      return;
    }
    if (isFormSpecItem(child.item)) {
      uuids.add(child.item.uuid);
    }
  });
  return uuids;
};

export const DUPLICATE_CHILD_MESSAGE =
  "Already in this BOM; adjust its quantity instead";

/**
 * Error for replacing the child at `bomIndex` with `replacement`, if that
 * item is already one of its siblings.
 */
export const duplicateSiblingError = (
  parent: ItemWithBOM | null | undefined,
  bomIndex: number,
  replacement: ItemWithBOM | null | undefined
): string | null => {
  if (!replacement || !isFormSpecItem(replacement.item)) {
    return null;
  }
  return bomChildItemUuids(parent, { excludeIndex: bomIndex }).has(
    replacement.item.uuid
  )
    ? `${replacement.item.name} is already in this BOM; adjust its quantity instead.`
    : null;
};
